import { t } from "@lingui/macro";
import { MagicWand } from "@phosphor-icons/react";
import { Plus, PlusCircle } from "@phosphor-icons/react";
import {
  Award,
  Certification,
  CustomSection,
  Education,
  Extracurricular,
  Experience,
  Interest,
  Internship,
  Language,
  Profile,
  Project,
  Publication,
  Reference,
  Skill,
  URL,
  Volunteer,
} from "@reactive-resume/schema";
import { useTheme } from "@reactive-resume/hooks";
import { Button, ScrollArea, Separator } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { Fragment, useRef } from "react";
import { Link } from "react-router-dom";

// import { Icon } from "@/client/components/icon";
import { UserAvatar } from "@/client/components/user-avatar";
import { UserOptions } from "@/client/components/user-options";
import { useIsCoachOptimizing } from "@/client/stores/coach-optimization";
import { useDialog } from "@/client/stores/dialog";
import { useResumeStore } from "@/client/stores/resume";

import { CoachReviewHeader } from "../../_components/coach/coach-review-header";
import { BasicsSection } from "./sections/basics";
import { SectionBase } from "./sections/shared/section-base";
import { SectionIcon } from "./sections/shared/section-icon";
import { SummarySection } from "./sections/summary";

const stripHtml = (value?: string) => {
  if (!value) return "";
  return value
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const getSummaryPreview = (value?: string, fallback = "") => {
  const text = stripHtml(value);
  if (!text) return fallback;
  return text.length > 60 ? `${text.slice(0, 57)}...` : text;
};

export const LeftSidebar = () => {
  const containterRef = useRef<HTMLDivElement | null>(null);
  const csvImportDialog = useDialog("ai-csv-import");
  const { isDarkMode } = useTheme();

  const addSection = useResumeStore((state) => state.addSection);
  const customSections = useResumeStore((state) => state.resume.data?.sections?.custom ?? {});

  const scrollIntoView = (selector: string) => {
    const section = containterRef.current?.querySelector(selector);
    section?.scrollIntoView({ behavior: "smooth" });
  };
  const currentTemplate = useResumeStore((state) => state.resume.data?.metadata?.template ?? "");
  const education = useResumeStore(
    (state) => state.resume.data?.sections?.education ?? { items: [] },
  );
  const isMastersUnion = currentTemplate === "mastersunion";
  const resumeId = useResumeStore((state) => state.resume.id);
  const isCoachOptimizing = useIsCoachOptimizing(resumeId);

  // Check if current template is "mastersunion"
  // const isAdvancedTemplate = currentTemplate === "mastersunion";

  // Hide Professional Summary for PGP in Technology & Business Management students
  const isPGPStudent = (education.items ?? []).some(
    (item) =>
      item.visible &&
      item.studyType &&
      item.studyType.toLowerCase().includes("pgp in technology") &&
      item.studyType.toLowerCase().includes("business management"),
  );

  // Hide Summary section for mastersunion template or PGP students
  // const shouldShowSummary = currentTemplate !== "mastersunion" && !isPGPStudent;
  const shouldHideMastersunionSection = (id: "profiles" | "interests" | "references") =>
    isMastersUnion && (id === "profiles" || id === "interests" || id === "references");

  // Sections this sidebar is NOT mounting. Coach suggestions render inside their own section, so
  // anything targeting a section that isn't on screen would be unreachable while still counting
  // towards "accept all" — CoachReviewHeader renders those few itself.
  const hiddenSectionIds = isMastersUnion
    ? ["profiles", "interests", "references", "languages"]
    : [];

  return (
    <div className="flex bg-secondary-accent/30">
      <div
        className={cn(
          "hidden basis-12 flex-col items-center justify-between sm:flex",
          isMastersUnion
            ? "dark:bg-[#0f0f12] bg-[#f0f0f0] p-2 absolute left-0 top-0 h-full z-20"
            : "bg-secondary-accent/30 py-4",
        )}
      >
        <Button asChild size="icon" variant="ghost" className="size-8 rounded-full">
          <Link to="/dashboard">
            {/* <Icon size={14} /> */}
            {!isMastersUnion && (
              <img
                src={isDarkMode ? "/icon/CoachLogoDark.svg" : "/icon/CoachLogoLight.svg"}
                width={14}
                height={14}
                alt="Coach LMS"
                className="rounded-sm"
              />
            )}
          </Link>
        </Button>

        <div className="flex flex-col items-center justify-center gap-y-2">
          <SectionIcon
            id="basics"
            onClick={() => scrollIntoView("#basics")}
            name={t({
              message: "Basics",
              context:
                "The basics section of a resume consists of User's Picture, Full Name, Location etc.",
            })}
          />
          <SectionIcon id="summary" onClick={() => scrollIntoView("#summary")} />
          <SectionIcon id="education" onClick={() => scrollIntoView("#education")} />
          <SectionIcon id="experience" onClick={() => scrollIntoView("#experience")} />
          <SectionIcon id="internship" onClick={() => scrollIntoView("#internship")} />
          <SectionIcon id="projects" onClick={() => scrollIntoView("#projects")} />
          <SectionIcon id="certifications" onClick={() => scrollIntoView("#certifications")} />
          <SectionIcon id="extracurriculars" onClick={() => scrollIntoView("#extracurriculars")} />
          <SectionIcon id="skills" onClick={() => scrollIntoView("#skills")} />
          <SectionIcon id="publications" onClick={() => scrollIntoView("#publications")} />
          <SectionIcon id="awards" onClick={() => scrollIntoView("#awards")} />
          {shouldHideMastersunionSection("interests") ? null : (
            <SectionIcon id="interests" onClick={() => scrollIntoView("#interests")} />
          )}
          <SectionIcon id="volunteer" onClick={() => scrollIntoView("#volunteer")} />
          {shouldHideMastersunionSection("references") ? null : (
            <SectionIcon id="references" onClick={() => scrollIntoView("#references")} />
          )}
          {isMastersUnion ? null : (
            <SectionIcon id="languages" onClick={() => scrollIntoView("#languages")} />
          )}
          {shouldHideMastersunionSection("profiles") ? null : (
            <SectionIcon id="profiles" onClick={() => scrollIntoView("#profiles")} />
          )}
          <SectionIcon
            id="custom"
            variant="ghost"
            name="AI CSV Import"
            icon={<MagicWand size={14} />}
            onClick={() => csvImportDialog.open("create")}
          />

          <SectionIcon
            id="custom"
            variant="outline"
            name={t`Add a new section`}
            icon={<Plus size={14} />}
            onClick={() => {
              addSection();
              // eslint-disable-next-line lingui/no-unlocalized-strings
              scrollIntoView("& > section:last-of-type");
            }}
          />
        </div>

        <UserOptions>
          <Button size="icon" variant="ghost" className="rounded-full">
            <UserAvatar size={28} />
          </Button>
        </UserOptions>
      </div>

      <ScrollArea
        orientation="vertical"
        className={cn("h-screen flex-1 pb-16 lg:pb-0", isMastersUnion && "left-6")}
      >
        {/* Extra headroom during a coach optimization only. The rail gains a job-context header at
            the top, and at p-6 it sat hard against the window chrome. */}
        <div
          ref={containterRef}
          className={cn("grid gap-y-6 p-6 @container/left", isCoachOptimizing && "pt-12")}
        >
          {/* Coach Resume optimization: context for the job being targeted. The suggestions
              themselves render inside their own sections below (see CoachSectionSuggestions),
              so a rewrite sits next to the content it replaces. Renders only while a coach
              session is active for THIS resume, so the normal builder is untouched. */}
          {isCoachOptimizing && (
            <>
              <CoachReviewHeader hiddenSectionIds={hiddenSectionIds} />
              <Separator />
            </>
          )}

          <BasicsSection />
          <Separator />
          <SummarySection />
          <SectionBase<Education>
            id="education"
            title={(item): string => {
              const institution = item.institution as string | URL | undefined;
              if (
                typeof institution === "object" &&
                institution !== null &&
                "label" in institution
              ) {
                return institution.label || institution.href || "";
              }
              return (institution as string) || "";
            }}
            description={(item) => item.area}
          />
          <Separator />
          <SectionBase<Experience>
            id="experience"
            title={(item): string => {
              const company = item.company as string | URL | undefined;
              if (typeof company === "object" && company !== null && "label" in company) {
                return company.label || company.href || "";
              }
              return (company as string) || "";
            }}
            description={(item) =>
              currentTemplate === "mastersunion"
                ? item.designations?.[0]?.title || item.position
                : item.position
            }
          />
          <Separator />
          <SectionBase<Experience>
            id="internship"
            title={(item): string => {
              const company = item.company as string | URL | undefined;
              if (typeof company === "object" && company !== null && "label" in company) {
                return company.label || company.href || "";
              }
              return (company as string) || "";
            }}
            description={(item) => item.position}
          />
          <Separator />
          <SectionBase<Project>
            id="projects"
            title={(item) => item.name}
            description={(item) => item.description}
          />
          <Separator />
          <SectionBase<Certification>
            id="certifications"
            title={(item) => item.name}
            description={(item) => item.issuer}
          />
          <Separator />
          <SectionBase<Extracurricular>
            id="extracurriculars"
            title={(item) =>
              currentTemplate === "mastersunion"
                ? item.organization || getSummaryPreview(item.summary, "Extra-curricular")
                : item.organization
            }
            description={(item) =>
              currentTemplate === "mastersunion"
                ? item.position || (item.organization ? getSummaryPreview(item.summary) : "")
                : item.position
            }
          />
          {isMastersUnion ? null : (
            <>
              <Separator />
              <SectionBase<Language>
                id="languages"
                title={(item) => item.name}
                description={(item) => item.description}
              />
              <Separator />
            </>
          )}
          <SectionBase<Skill>
            id="skills"
            title={(item) => item.name}
            description={(item) => {
              if (item.description) return item.description;
              if ((item.keywords?.length ?? 0) > 0) return `${item.keywords.length} keywords`;
            }}
          />
          {shouldHideMastersunionSection("profiles") ? null : (
            <>
              <Separator />
              <SectionBase<Profile>
                id="profiles"
                title={(item) => item.network}
                description={(item) => item.username}
              />
            </>
          )}
          <Separator />
          <SectionBase<Award>
            id="awards"
            title={(item) => (currentTemplate === "mastersunion" ? "Achievement" : item.title)}
            description={(item) => (currentTemplate === "mastersunion" ? item.date : item.awarder)}
          />

          {shouldHideMastersunionSection("interests") ? null : (
            <>
              <Separator />
              <SectionBase<Interest>
                id="interests"
                title={(item) => item.name}
                description={(item) => {
                  if ((item.keywords?.length ?? 0) > 0) return `${item.keywords.length} keywords`;
                }}
              />
            </>
          )}
          <Separator />
          <SectionBase<Publication>
            id="publications"
            title={(item) => item.name}
            description={(item) => item.publisher}
          />
          <Separator />
          <SectionBase<Volunteer>
            id="volunteer"
            title={(item) => item.organization}
            description={(item) => item.position}
          />
          {shouldHideMastersunionSection("references") ? null : (
            <>
              <Separator />
              <SectionBase<Reference>
                id="references"
                title={(item) => item.name}
                description={(item) => item.description}
              />
            </>
          )}

          {/* Custom Sections */}
          {Object.values(customSections).map((section) => (
            <Fragment key={section.id}>
              <Separator />

              <SectionBase<CustomSection>
                id={`custom.${section.id}`}
                title={(item) => item.name}
                description={(item) => item.description}
                summary={(item) => item.summary}
              />
            </Fragment>
          ))}

          <Separator />

          <Button size="lg" variant="outline" onClick={addSection}>
            <PlusCircle />
            <span className="ml-2">{t`Add a new section`}</span>
          </Button>
        </div>
      </ScrollArea>
    </div>
  );
};
